import { OSMap, OSMarker, OSRoute, RoutalPalette } from '@nx-smartmonkey/ui';
import { LatLng, LatLngTuple } from 'leaflet';
import { capacitorService } from '../../../../application/CapacitorService';
import { STOP_STATUS } from '../../../../constants/Stops';
import { Route } from '../../../../domain/route/Route';
import { Stop } from '../../../../domain/stop/Stop';
import { useLocationContext } from '../../context/locationContext/context';

export interface ReorderMapStops {
  selectedStops?: string[]; // This is for Reorder, this changes the color of a stop to secondary
  reorderedStops?: Stop[]; // This is for Reorder, This is the list of all the stops and its new order
}

interface CreateAndReorderStopsMapProps {
  selectedRoute: Route;
  addedStops?: Stop[]; // This is for Stop Creation, this adds a new stop
  mapHeight: number;
  reorderMapStops?: ReorderMapStops;
  onLongClickAddMoreStops?: (latlng: LatLng) => void;
  onMarkerClick?: (stop: Stop) => void;
}

export const CreateAndReorderStopsMap = ({
  selectedRoute,
  mapHeight,
  addedStops,
  reorderMapStops,
  onLongClickAddMoreStops,
  onMarkerClick,
}: CreateAndReorderStopsMapProps) => {
  const { locationState } = useLocationContext();

  let coordsRouteBounds = [
    ...selectedRoute.stops
      .map((point) => (point.location ? ([point.location.lat, point.location.lng] as LatLngTuple) : null))
      .filter((coord): coord is LatLngTuple => coord !== null),
    ...(selectedRoute.start_location
      ? [[selectedRoute.start_location.lat, selectedRoute.start_location.lng] as LatLngTuple]
      : []),
    ...(selectedRoute.end_location
      ? [[selectedRoute.end_location.lat, selectedRoute.end_location.lng] as LatLngTuple]
      : []),
    ...(addedStops
      ? addedStops
          .filter((stop) => stop.location !== undefined)
          .map((stop) => [stop.location!.lat, stop.location!.lng] as LatLngTuple)
      : []),
  ];

  // Filter out null and undefined values
  coordsRouteBounds = coordsRouteBounds.filter((coord): coord is LatLngTuple => coord !== null && coord !== undefined);

  const bounds = {
    max: [
      Math.max(...coordsRouteBounds.map((coord) => coord[0])),
      Math.max(...coordsRouteBounds.map((coord) => coord[1])),
    ] as LatLngTuple,
    min: [
      Math.min(...coordsRouteBounds.map((coord) => coord[0])),
      Math.min(...coordsRouteBounds.map((coord) => coord[1])),
    ] as LatLngTuple,
  };

  return (
    <OSMap
      height={`${mapHeight}px`}
      zoomControl={false}
      touchZoom
      dragging
      centerZoomLevel={14}
      onLongClick={onLongClickAddMoreStops}
      routeBounds={[bounds.min, bounds.max]}
      tapHold={capacitorService.isIOS()}
    >
      {locationState.currentPosition && (
        <OSMarker
          id={`current-position-${selectedRoute.id}`}
          key={`current-position-${selectedRoute.id}`}
          doNotRefreshBounds
          lat={locationState.currentPosition.lat}
          lng={locationState.currentPosition.lng}
          kind={`routal_truck`}
          color={RoutalPalette.routeBaseColor}
        />
      )}
      {addedStops?.map((stop: Stop, idx: number) => {
        if (!stop.location?.isValid() || stop.location.lat === undefined || stop.location.lng === undefined) {
          return null;
        }

        return (
          <OSMarker
            id={`current-position-${selectedRoute.id}`}
            key={`current-position-${selectedRoute.id}`}
            doNotRefreshBounds
            lat={stop.location.lat}
            lng={stop.location.lng}
            kind={`routal_created_stop`}
            color={RoutalPalette.routeBaseColor}
            onClick={() => onMarkerClick && onMarkerClick(stop)}
          />
        );
      })}
      <OSRoute id={`driver-${selectedRoute.id}`} key={selectedRoute.id} path={selectedRoute.planned_track}>
        {reorderMapStops &&
          reorderMapStops.reorderedStops?.map((stop: Stop, idx: number) => {
            if (!stop.location?.isValid()) {
              return null;
            }
            return (
              <OSMarker
                id={stop.id}
                key={stop.id}
                number={idx + 1}
                done={stop.status === STOP_STATUS.completed}
                incomplete={stop.status === STOP_STATUS.incomplete}
                error={stop.status === STOP_STATUS.canceled}
                reorder={reorderMapStops.selectedStops?.includes(stop.id)}
                lat={stop.location!.lat as number}
                lng={stop.location!.lng as number}
                showWarning={stop.optimization_warning}
                kind={stop.type === `pickup` ? `routal_pickup` : `routal_default`}
                onClick={() => onMarkerClick && onMarkerClick(stop)}
              />
            );
          })}
        {!reorderMapStops?.reorderedStops &&
          selectedRoute.stops.map((stop: Stop, idx: number) => {
            if (!stop.location?.isValid()) {
              return null;
            }
            return (
              <OSMarker
                id={stop.id}
                key={stop.id}
                number={idx + 1}
                done={stop.status === STOP_STATUS.completed}
                incomplete={stop.status === STOP_STATUS.incomplete}
                error={stop.status === STOP_STATUS.canceled}
                lat={stop.location!.lat as number}
                lng={stop.location!.lng as number}
                showWarning={stop.optimization_warning}
                kind={stop.type === `pickup` ? `routal_pickup` : `routal_default`}
                onClick={() => onMarkerClick && onMarkerClick(stop)}
              />
            );
          })}
        {selectedRoute.start_location?.isValid() && (
          <OSMarker
            id={`start-location-${selectedRoute.id}`}
            key={`start-location-${selectedRoute.id}`}
            lat={selectedRoute.start_location.lat!}
            lng={selectedRoute.start_location.lng!}
            kind="start"
          />
        )}
        {selectedRoute.end_location?.isValid() &&
          selectedRoute.end_location.lat !== selectedRoute.start_location?.lat &&
          selectedRoute.end_location.lng !== selectedRoute.start_location?.lng && (
            <OSMarker
              id={`end-location-${selectedRoute.id}`}
              key={`end-location-${selectedRoute.id}`}
              lat={selectedRoute.end_location.lat!}
              lng={selectedRoute.end_location.lng!}
              kind="end"
            />
          )}
      </OSRoute>
    </OSMap>
  );
};
